/**
 * @file The mechanism behind a per-key live tuning layer.
 *
 * Two tables ship balance keyed by name — `WEAPONS` and `ENEMIES` — and both are
 * frozen on purpose: they are what the build ships with. Both also need the same
 * editable copy on top: a sparse map of numeric overrides per `key.path`,
 * persisted in `localStorage`, merged back into a read-only view that gameplay
 * reads through, plus the row shape the debug window renders.
 *
 * That mechanism lives here once. {@link ./WeaponTuning.js} and
 * {@link ./EnemyTuning.js} are what remain of them: a `*_META` table of editing
 * rules, a storage key, and the two decisions that genuinely differ — which
 * leaves are editable, and which rows the panel lists.
 *
 * The single-tree layers ({@link ./PlayerTuning.js}, {@link ./CameraTuning.js})
 * override one config object rather than a table of keyed ones, so they keep
 * their own copies of the same idea; folding them in would mean bending this
 * shape around a different one.
 */

import { humanise } from '../utils/Text.js';

/**
 * @typedef {object} FieldMeta
 * @property {string} label Human-readable name for the debug panel.
 * @property {number} step Numeric input step.
 * @property {number} min Lowest accepted value.
 * @property {boolean} [integer] Whether edits are rounded to whole numbers.
 */

/**
 * @typedef {object} TuningRow
 * @property {string} key Table key the row belongs to.
 * @property {string} name Display name from the table.
 * @property {{ path: string, label: string, value: number, defaultValue: number,
 *   step: number, min: number, integer: boolean, overridden: boolean }[]} fields
 */

/**
 * Builds a per-key tuning layer over a frozen table.
 *
 * @param {object} options Layer definition.
 * @param {Record<string, any>} options.table The shipped table; never mutated.
 * @param {string} options.storageKey `localStorage` key for this layer's overrides.
 * @param {Readonly<Record<string, FieldMeta>>} options.meta Per-path editing rules.
 * @param {FieldMeta} options.defaultMeta Rules for a path `meta` does not cover.
 * @param {(path: string, key: string) => boolean} options.isSkipped True for a
 *   numeric leaf that must never be editable — packed colours, masks, layers.
 * @param {(key: string, entry: any) => boolean} [options.isInspectable] Which
 *   table entries the debug panel lists; defaults to all of them.
 * @param {string} options.exportLabel Key the merged table is exported under.
 * @returns {object} The layer's API, named generically; the wrapping module
 *   re-exports it under its own nouns.
 */
export function createKeyedTuningLayer({
  table,
  storageKey,
  meta,
  defaultMeta,
  isSkipped,
  isInspectable = () => true,
  exportLabel
}) {
  /** Overrides only, sparse, `key` -> `path` -> value. @type {Record<string, Record<string, number>>} */
  let overrides = {};
  /** Memoised merge of `table` + {@link overrides}. @type {Record<string, object> | null} */
  let merged = null;
  /** @type {Set<(tuning: Record<string, object>) => void>} */
  const listeners = new Set();
  /** @type {Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null} */
  let storage = detectStorage();

  /**
   * @param {string} key Table key.
   * @param {string} prefix Dotted path of `node` within the entry.
   * @param {Record<string, any>} node Sub-tree being merged.
   * @returns {object} A frozen merge of `node` and the matching overrides.
   */
  function buildMergedNode(key, prefix, node) {
    /** @type {Record<string, any>} */
    const result = {};
    for (const [field, value] of Object.entries(node)) {
      const path = prefix ? `${prefix}.${field}` : field;
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        result[field] = buildMergedNode(key, path, value);
      } else if (isTunable(value, path, field)) {
        result[field] = overrides[key]?.[path] ?? value;
      } else {
        // Names, behaviours, colours, flags: not tunable, passed through untouched.
        result[field] = value;
      }
    }
    return Object.freeze(result);
  }

  /**
   * @param {any} value Leaf value.
   * @param {string} path Its dotted path.
   * @param {string} field Its own key.
   * @returns {boolean} True when the leaf is editable as a number.
   */
  function isTunable(value, path, field) {
    return typeof value === 'number' && Number.isFinite(value) && !isSkipped(path, field);
  }

  /** @returns {void} Drops the memoised merge and notifies subscribers. */
  function invalidate() {
    merged = null;
    const tuning = getTuning();
    for (const listener of listeners) listener(tuning);
  }

  /**
   * Merged, read-only view of the table. This is what gameplay reads.
   * @returns {Record<string, object>} The table with current overrides applied.
   */
  function getTuning() {
    if (!merged) {
      merged = {};
      for (const [key, entry] of Object.entries(table)) {
        merged[key] = buildMergedNode(key, '', entry);
      }
      Object.freeze(merged);
    }
    return merged;
  }

  /**
   * Every overridable field of one entry, as dotted paths. Derived from the table
   * itself, so a new numeric field becomes tunable by existing.
   *
   * @param {string} key Table key.
   * @returns {string[]} Dotted paths, deepest last.
   */
  function tuningPaths(key) {
    const entry = table[key];
    if (!entry) return [];
    /** @type {string[]} */
    const paths = [];
    const walk = (prefix, node) => {
      for (const [field, value] of Object.entries(node)) {
        const path = prefix ? `${prefix}.${field}` : field;
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) walk(path, value);
        else if (isTunable(value, path, field)) paths.push(path);
      }
    };
    walk('', entry);
    return paths;
  }

  /**
   * @param {string} path Field path.
   * @returns {FieldMeta} Editing rules, humanised when the table has no entry.
   */
  function getFieldMeta(path) {
    return meta[path] ?? { ...defaultMeta, label: humanise(path.split('.').pop() ?? path) };
  }

  /**
   * @param {string} key Table key.
   * @param {string} path Field path.
   * @returns {number | undefined} The shipped value, ignoring overrides.
   */
  function getDefaultValue(key, path) {
    const value = readPath(table[key], path);
    const leaf = path.split('.').pop() ?? path;
    return isTunable(value, path, leaf) ? value : undefined;
  }

  /**
   * Applies one edit. Takes effect the next time gameplay reads the value —
   * fields baked onto a component at spawn or equip therefore land on the next
   * one built, not on what is already in the level.
   *
   * @param {string} key Table key.
   * @param {string} path Field path.
   * @param {number | string} rawValue New value; strings come straight from an input.
   * @returns {boolean} False when the path is unknown or the value is not finite.
   */
  function setValue(key, path, rawValue) {
    const fallback = getDefaultValue(key, path);
    if (fallback === undefined) return false;

    const parsed = typeof rawValue === 'number' ? rawValue : Number.parseFloat(String(rawValue));
    if (!Number.isFinite(parsed)) return false;

    const rules = getFieldMeta(path);
    let value = Math.max(rules.min, parsed);
    if (rules.integer) value = Math.round(value);

    if (!overrides[key]) overrides[key] = {};
    // Storing a value equal to the default would persist noise and make a reset
    // to code defaults indistinguishable from an explicit edit.
    if (value === fallback) delete overrides[key][path];
    else overrides[key][path] = value;
    if (Object.keys(overrides[key]).length === 0) delete overrides[key];

    invalidate();
    return true;
  }

  /**
   * Replaces every override at once. Unknown or invalid entries are skipped, so a
   * stale saved payload can never break a build that renamed a field.
   *
   * @param {Record<string, Record<string, number>>} next Override map.
   * @returns {number} How many entries were applied.
   */
  function applyOverrides(next) {
    overrides = {};
    let applied = 0;

    for (const [key, values] of Object.entries(next ?? {})) {
      if (!table[key] || !values || typeof values !== 'object') continue;
      for (const [path, value] of Object.entries(values)) {
        const fallback = getDefaultValue(key, path);
        if (fallback === undefined || typeof value !== 'number' || !Number.isFinite(value)) continue;
        const rules = getFieldMeta(path);
        const clamped = rules.integer
          ? Math.round(Math.max(rules.min, value))
          : Math.max(rules.min, value);
        if (clamped === fallback) continue;
        if (!overrides[key]) overrides[key] = {};
        overrides[key][path] = clamped;
        applied++;
      }
    }

    invalidate();
    return applied;
  }

  /**
   * Rows for the debug window, one per listed entry. Consumed 1:1 by the UI, so
   * extending the table extends the window with no UI edit.
   * @returns {TuningRow[]}
   */
  function inspect() {
    const tuning = getTuning();
    return Object.entries(table)
      .filter(([key, entry]) => isInspectable(key, entry))
      .map(([key, entry]) => ({
        key,
        name: entry.name,
        fields: tuningPaths(key).map((path) => {
          const rules = getFieldMeta(path);
          return {
            path,
            label: rules.label || humanise(path.split('.').pop() ?? path),
            value: /** @type {number} */ (readPath(tuning[key], path)),
            defaultValue: /** @type {number} */ (getDefaultValue(key, path)),
            step: rules.step,
            min: rules.min,
            integer: rules.integer === true,
            overridden: isValueOverridden(key, path)
          };
        })
      }));
  }

  /**
   * @param {string} key Table key.
   * @param {string} path Field path.
   * @returns {boolean} True when this field was edited away from the default.
   */
  function isValueOverridden(key, path) {
    return path in (overrides[key] ?? {});
  }

  return {
    setStorage(next) {
      storage = next;
    },
    getTuning,
    getEntry(key) {
      return getTuning()[key] ?? null;
    },
    tuningPaths,
    getFieldMeta,
    getDefaultValue,
    setValue,
    getOverrides() {
      return Object.fromEntries(
        Object.entries(overrides).map(([key, values]) => [key, { ...values }])
      );
    },
    hasOverrides() {
      return Object.keys(overrides).length > 0;
    },
    isValueOverridden,
    applyOverrides,
    save() {
      if (!storage) return false;
      try {
        storage.setItem(storageKey, JSON.stringify(overrides));
        return true;
      } catch {
        return false;
      }
    },
    load() {
      if (!storage) return 0;
      try {
        const raw = storage.getItem(storageKey);
        if (!raw) return 0;
        return applyOverrides(JSON.parse(raw));
      } catch {
        // A corrupt payload must never keep the game from booting.
        return 0;
      }
    },
    reset() {
      overrides = {};
      try {
        storage?.removeItem(storageKey);
      } catch {
        // The in-memory reset already succeeded.
      }
      invalidate();
    },
    export() {
      return JSON.stringify(
        { overrides: this.getOverrides(), [exportLabel]: getTuning() },
        null,
        2
      );
    },
    onChanged(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    inspect
  };
}

/**
 * @returns {Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null} The
 *   browser store, or null where there is none (tests, SSR, blocked cookies).
 */
function detectStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Safari throws instead of returning null when storage is blocked.
    return null;
  }
}

/**
 * @param {Record<string, any> | undefined} root Tree to read from.
 * @param {string} path Dotted path.
 * @returns {any} The value at `path`, or undefined if a segment is missing.
 */
function readPath(root, path) {
  return path.split('.').reduce((node, seg) => (node == null ? undefined : node[seg]), root);
}
